import React, { useEffect, useRef } from "react";
import Button from "../../components/ui/Button";
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const StrategySection = () => {
  // 1. Create refs for the main elements
  const sectionRef = useRef(null);
  const leftContentRef = useRef(null);
  const rightImageRef = useRef(null);

  useEffect(() => {
    // 2. Set up GSAP context
    const context = gsap.context(() => {
      // Define the common ScrollTrigger settings for the section's overall entry
      const triggerSettings = {
        trigger: sectionRef.current,
        start: "top 85%", // Animation starts when section hits 85% down the viewport
        end: "center 50%", // Animation completes by the time the section is near the center
        scrub: 1, // Smooth scrubbing effect
        // markers: true, // Uncomment for debugging
      };

      // ----------------------------------------------------
      // A. Left Content Block Animation (Slide in from Left)
      // ----------------------------------------------------
      if (leftContentRef.current) {
        gsap.from(leftContentRef.current, {
          x: -100, // Start 100px off to the left
          opacity: 0,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // B. Right Image Animation (Fade in)
      // ----------------------------------------------------
      if (rightImageRef.current) {
        gsap.from(rightImageRef.current, {
          opacity: 0,
          scale: 0.95,
          ease: "power2.out",
          scrollTrigger: triggerSettings,
        });
      }

      // ----------------------------------------------------
      // C. Inner Text Reveal Animation (Staggered Reveal)
      // ----------------------------------------------------
      const textElements = gsap.utils.toArray(
        leftContentRef.current.querySelectorAll(".strategy-text-item"),
      );

      const innerTriggerSettings = {
        trigger: leftContentRef.current, // Use the left block as the trigger
        start: "top 85%",
        end: "center 60%",
        scrub: 1,
      };

      textElements.forEach((element, index) => {
        // Initial State: Hidden (opacity: 0), slightly down (y: 20), clipped
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        gsap.to(element, {
          y: 0, // Final state: Original position (move up)
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1, // Stagger effect for title, paragraph, and button
          scrollTrigger: innerTriggerSettings,
        });
      });

      // ----------------------------------------------------
      // D. Small Decorative Images (Simple Fade/Slide)
      // ----------------------------------------------------
      const leafImage = leftContentRef.current.querySelector(".leaf-image");
      const panImage = leftContentRef.current.querySelector(".pan-image");

      if (leafImage) {
        gsap.from(leafImage, {
          y: -50,
          opacity: 0,
          duration: 1.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: leftContentRef.current,
            start: "top 95%",
            toggleActions: "play none none reverse",
          },
        });
      }

      if (panImage) {
        gsap.from(panImage, {
          y: 50,
          opacity: 0,
          duration: 1.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: leftContentRef.current,
            start: "top 95%",
            toggleActions: "play none none reverse",
          },
        });
      }
    }, sectionRef);

    // 3. Cleanup
    return () => context.revert();
  }, []);

  return (
    <section
      ref={sectionRef} // Attach section ref
      className="w-full flex flex-col-reverse lg:grid lg:grid-cols-2"
    >
      {/* LEFT SIDE - Attach leftContentRef */}
      <div ref={leftContentRef} className="relative bg-primary-accent">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DCFBB0] to-green-200 opacity-80">
          <img
            src="/assets/AboutPageImg/leef.png"
            className="leaf-image absolute top-[0px] left-[0px] w-[44%] " // Added leaf-image class
            alt="Decorative leaf"
          />
        </div>

        <div className="relative pl-[30px] xl:pl-[150px] pr-[30px] xl:pr-[42px] py-[30px] xl:py-[122px] h-[100%]">
          <div className="relative z-10">
            {/* Header - Added strategy-text-item class */}
            <h2 className="strategy-text-item text-[30px] xl:text-[100px] font-bold text-secondary-background font-['Parkinsans']">
              Our Strategy
            </h2>

            {/* Paragraph - Added strategy-text-item class */}
            <p className="strategy-text-item text-[20px] xl:text-[24px] font-light text-secondary-background mt-4 font-['Poppins']">
              UTPANN’s strategy is built on closing the natural resource loop
              through an integrated waste-to-wealth approach. By scientifically
              processing organic waste through advanced biogas technology at
              Govardhannathji Energies LLP, we convert waste into clean energy
              while significantly reducing methane and carbon emissions. The
              nutrient-rich by-products of this process are further transformed
              into high- quality organic fertilizers, supporting sustainable
              agriculture, responsible waste management, lower energy
              dependence, and long-term environmental protection.
            </p>

            {/* Button - Wrapped button in a div and added strategy-text-item class */}
            {/* <div className="strategy-text-item">
              <Button
                text="Learn More"
                text_font_size="text-[16px] sm:text-[20px] xl:text-[24px]"
                padding="t=12px,r=32px,b=12px,l=32px"
                className="hover:bg-secondary-background hover:text-white text-[#1c233a] transition-colors duration-300 mt-6"
              />
            </div> */}
          </div>
          <div>
            <img
              src="/assets/AboutPageImg/pan.png"
              className="pan-image absolute bottom-[0px] right-[0px] w-[15%] lg:w-[30%]" // Added pan-image class
              alt="Decorative pan"
            />
          </div>
        </div>
      </div>

      {/* RIGHT SIDE IMAGES - Attach rightImageRef */}
      <div className="relative bg-cover bg-center ">
        <img
          ref={rightImageRef}
          src="/assets/AboutPageImg/Strategy.png"
          className="w-full h-full"
          alt="Strategy background"
        />
      </div>
    </section>
  );
};

export default StrategySection;
